/**
 * Operator-to-operator messages (Admin / Production).
 * Collections: operatorMessages/{id} + users/{uid}/operatorMessageReceipts/{id}
 */

import {
    collection,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    where,
    writeBatch,
    Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import {
    parseRolesFromUserData,
    userHasAdminOrProductionRole,
} from './adminDesktopNotificationService';
import { sanitizeOperatorMessageHtml } from '../utils/operatorMessageSanitize';

export const OPERATOR_MESSAGES_COLL = 'operatorMessages';
export const OPERATOR_RECEIPTS_SUBCOLL = 'operatorMessageReceipts';

export const MESSAGE_STATUS = {
    SENT: 'sent',
    DELIVERED: 'delivered',
    READ: 'read',
};

/** Compose presets: show attention modal only once the recipient is online at/after this delay. */
export const OPERATOR_SHOW_AFTER_PRESETS = [
    { id: 'now', label: 'Now', minutes: 0 },
    { id: '15m', label: 'In 15 min', minutes: 15 },
    { id: '1h', label: 'In 1 hour', minutes: 60 },
    { id: '2h', label: 'In 2 hours', minutes: 120 },
    { id: '4h', label: 'In 4 hours', minutes: 240 },
    { id: 'tomorrow_am', label: 'Tomorrow 9:00', minutes: null },
];

function displayNameFromUser(data, fallbackUid) {
    if (!data) return fallbackUid || 'Unknown';
    const name =
        data.name ||
        data.displayName ||
        data.companyName ||
        data.username ||
        data.email ||
        fallbackUid;
    return String(name).trim() || fallbackUid || 'Unknown';
}

function mapReceiptDoc(snap) {
    const data = snap.data() || {};
    return {
        id: snap.id,
        ...data,
        createdAt: data.createdAt || null,
        deliveredAt: data.deliveredAt || null,
        readAt: data.readAt || null,
        showAfter: data.showAfter || null,
    };
}

function mapMessageDoc(snap) {
    const data = snap.data() || {};
    return {
        id: snap.id,
        ...data,
        createdAt: data.createdAt || null,
        readAt: data.readAt || null,
        showAfter: data.showAfter || null,
    };
}

function sortByCreatedDesc(a, b) {
    const ta = toMillis(a.createdAt) || toMillis(a.deliveredAt) || 0;
    const tb = toMillis(b.createdAt) || toMillis(b.deliveredAt) || 0;
    return tb - ta;
}

function sortByDeliveredAsc(a, b) {
    const ta = toMillis(a.showAfter) || toMillis(a.deliveredAt) || toMillis(a.createdAt) || 0;
    const tb = toMillis(b.showAfter) || toMillis(b.deliveredAt) || toMillis(b.createdAt) || 0;
    return ta - tb;
}

function toMillis(value) {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (value instanceof Timestamp) return value.toMillis();
    if (typeof value.seconds === 'number') return value.seconds * 1000;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Resolve a compose preset to a Firestore Timestamp, or null for immediate show.
 * @param {string} [presetId]
 * @returns {import('firebase/firestore').Timestamp | null}
 */
export function resolveShowAfterTimestamp(presetId) {
    const preset =
        OPERATOR_SHOW_AFTER_PRESETS.find((p) => p.id === presetId) || OPERATOR_SHOW_AFTER_PRESETS[0];
    if (!preset || preset.id === 'now' || preset.minutes === 0) return null;

    if (preset.id === 'tomorrow_am') {
        const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Europe/London',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(new Date());
        const get = (type) => Number(parts.find((p) => p.type === type)?.value || 0);
        const y = get('year');
        const m = get('month');
        const d = get('day');
        const noonUtc = Date.UTC(y, m - 1, d, 12, 0, 0);
        const tomorrow = new Date(noonUtc + 24 * 60 * 60 * 1000);
        const tParts = new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Europe/London',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(tomorrow);
        const ty = Number(tParts.find((p) => p.type === 'year')?.value);
        const tm = Number(tParts.find((p) => p.type === 'month')?.value);
        const td = Number(tParts.find((p) => p.type === 'day')?.value);
        let guess = Date.UTC(ty, tm - 1, td, 8, 0, 0);
        for (let i = 0; i < 4; i += 1) {
            const local = new Intl.DateTimeFormat('en-GB', {
                timeZone: 'Europe/London',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            }).formatToParts(new Date(guess));
            const ly = Number(local.find((p) => p.type === 'year')?.value);
            const lm = Number(local.find((p) => p.type === 'month')?.value);
            const ld = Number(local.find((p) => p.type === 'day')?.value);
            const lh = Number(local.find((p) => p.type === 'hour')?.value);
            const lmin = Number(local.find((p) => p.type === 'minute')?.value);
            const target = Date.UTC(ty, tm - 1, td, 9, 0, 0);
            const actualAsIf = Date.UTC(ly, lm - 1, ld, lh, lmin, 0);
            guess += target - actualAsIf;
        }
        return Timestamp.fromMillis(guess);
    }

    const minutes = Number(preset.minutes) || 0;
    if (minutes <= 0) return null;
    return Timestamp.fromMillis(Date.now() + minutes * 60 * 1000);
}

/** True when the note may interrupt the recipient (no timer, or timer has elapsed). */
export function isOperatorMessageDue(item, nowMs = Date.now()) {
    if (!item) return false;
    const showMs = toMillis(item.showAfter);
    if (!showMs) return true;
    return showMs <= nowMs;
}

export function countDueUnread(items, nowMs = Date.now()) {
    if (!Array.isArray(items)) return 0;
    return items.filter(
        (item) => item.status === MESSAGE_STATUS.DELIVERED && isOperatorMessageDue(item, nowMs)
    ).length;
}

/**
 * Active Admin / Production users. Current user is pinned first as a self-note option.
 * @param {string} [currentUid] — when set, that user is listed first as "Note to myself"
 * @returns {Promise<Array<{ uid: string, name: string, email: string, role: string, isSelf?: boolean }>>}
 */
export async function listEligibleRecipients(currentUid) {
    const snap = await getDocs(collection(db, 'users'));
    const others = [];
    let selfEntry = null;
    snap.forEach((userSnap) => {
        const data = userSnap.data() || {};
        if (data.active === false) return;
        if (!userHasAdminOrProductionRole(data)) return;
        const entry = {
            uid: userSnap.id,
            name: displayNameFromUser(data, userSnap.id),
            email: data.email || data.username || '',
            role: typeof data.role === 'string' ? data.role : parseRolesFromUserData(data).join(', '),
            isSelf: false,
        };
        if (currentUid && userSnap.id === currentUid) {
            selfEntry = {
                ...entry,
                isSelf: true,
                name: 'Note to myself',
            };
            return;
        }
        others.push(entry);
    });
    others.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    if (selfEntry) return [selfEntry, ...others];
    return others;
}

/**
 * @param {{
 *   fromUid: string,
 *   fromUserData?: Record<string, unknown>,
 *   toUid: string,
 *   toName?: string,
 *   bodyHtml: string,
 *   showAfterPresetId?: string,
 * }} params
 */
export async function sendOperatorMessage({
    fromUid,
    fromUserData,
    toUid,
    toName,
    bodyHtml,
    showAfterPresetId = 'now',
}) {
    if (!fromUid || !toUid) {
        throw new Error('Sender and recipient are required.');
    }
    if (!userHasAdminOrProductionRole(fromUserData || {})) {
        throw new Error('Only Admin and Production can send operator messages.');
    }

    const { bodyHtml: cleanHtml, bodyText } = sanitizeOperatorMessageHtml(bodyHtml);
    if (!bodyText) {
        throw new Error('Message cannot be empty.');
    }

    const isSelfNote = fromUid === toUid;
    const fromName = displayNameFromUser(fromUserData, fromUid);
    const resolvedToName = isSelfNote ? 'Note to myself' : toName || toUid;
    const showAfter = resolveShowAfterTimestamp(showAfterPresetId);
    const messageRef = doc(collection(db, OPERATOR_MESSAGES_COLL));
    const receiptRef = doc(db, 'users', toUid, OPERATOR_RECEIPTS_SUBCOLL, messageRef.id);
    const now = serverTimestamp();

    const messagePayload = {
        fromUid,
        fromName,
        toUid,
        toName: resolvedToName,
        isSelfNote,
        bodyHtml: cleanHtml,
        bodyText,
        createdAt: now,
        showAfter: showAfter || null,
        showAfterPresetId: showAfterPresetId || 'now',
        status: MESSAGE_STATUS.SENT,
        readAt: null,
    };

    const receiptPayload = {
        messageId: messageRef.id,
        fromUid,
        fromName,
        toUid,
        toName: resolvedToName,
        isSelfNote,
        bodyHtml: cleanHtml,
        bodyText,
        createdAt: now,
        deliveredAt: now,
        showAfter: showAfter || null,
        showAfterPresetId: showAfterPresetId || 'now',
        status: MESSAGE_STATUS.DELIVERED,
        readAt: null,
    };

    const batch = writeBatch(db);
    batch.set(messageRef, messagePayload);
    batch.set(receiptRef, receiptPayload);
    await batch.commit();

    return { messageId: messageRef.id, showAfter };
}

/**
 * Mark a receipt read and mirror status on legacy parent message / chat unread.
 * @param {string} recipientUid
 * @param {string} messageId
 */
export async function markReceiptRead(recipientUid, messageId) {
    if (!recipientUid || !messageId) return;

    const receiptRef = doc(db, 'users', recipientUid, OPERATOR_RECEIPTS_SUBCOLL, messageId);
    const messageRef = doc(db, OPERATOR_MESSAGES_COLL, messageId);
    const now = serverTimestamp();

    let chatId = null;
    try {
        const receiptSnap = await getDoc(receiptRef);
        if (receiptSnap.exists()) {
            chatId = receiptSnap.data()?.chatId || null;
        }
    } catch {
        /* ignore */
    }

    await updateDoc(receiptRef, {
        status: MESSAGE_STATUS.READ,
        readAt: now,
    });

    try {
        await updateDoc(messageRef, {
            status: MESSAGE_STATUS.READ,
            readAt: now,
        });
    } catch (err) {
        // Legacy one-shot parent may be absent for Slack-lite messages; receipt is SSOT for Got it.
        console.warn('[operatorMessage] Could not update parent message status:', err?.message || err);
    }

    if (chatId) {
        try {
            const stateRef = doc(db, 'users', recipientUid, 'operatorChatState', chatId);
            await setDoc(
                stateRef,
                {
                    chatId,
                    unreadCount: 0,
                    lastReadAt: now,
                    updatedAt: now,
                },
                { merge: true }
            );
        } catch (err) {
            console.warn('[operatorMessage] Could not clear chat unread:', err?.message || err);
        }
    }
}

/**
 * @param {string} uid
 * @param {(items: Array) => void} onChange
 * @returns {() => void}
 */
export function subscribeMyInbox(uid, onChange) {
    if (!uid) {
        onChange([]);
        return () => {};
    }
    const ref = collection(db, 'users', uid, OPERATOR_RECEIPTS_SUBCOLL);
    return onSnapshot(
        ref,
        (snap) => {
            const items = snap.docs.map(mapReceiptDoc).sort(sortByCreatedDesc);
            onChange(items);
        },
        (err) => {
            console.error('[operatorMessage] inbox subscribe failed:', err);
            onChange([]);
        }
    );
}

/**
 * Unread receipts that are due to show (timer elapsed or none), oldest-first.
 * Re-emits when a future `showAfter` becomes due while the tab stays open.
 * @param {string} uid
 * @param {(items: Array) => void} onChange
 * @returns {() => void}
 */
export function subscribeUnreadInbox(uid, onChange) {
    let latestItems = [];
    let timerId = null;

    const clearTimer = () => {
        if (timerId != null) {
            clearTimeout(timerId);
            timerId = null;
        }
    };

    const emit = () => {
        const now = Date.now();
        const unread = latestItems
            .filter(
                (item) =>
                    item.status === MESSAGE_STATUS.DELIVERED && isOperatorMessageDue(item, now)
            )
            .sort(sortByDeliveredAsc);
        onChange(unread);

        clearTimer();
        const nextMs = latestItems
            .filter(
                (item) =>
                    item.status === MESSAGE_STATUS.DELIVERED && !isOperatorMessageDue(item, now)
            )
            .map((item) => toMillis(item.showAfter))
            .filter((ms) => ms > now);
        if (!nextMs.length) return;
        const delay = Math.min(Math.max(Math.min(...nextMs) - now + 250, 250), 2147483647);
        timerId = setTimeout(emit, delay);
    };

    const unsub = subscribeMyInbox(uid, (items) => {
        latestItems = items;
        emit();
    });

    return () => {
        clearTimer();
        unsub();
    };
}

/**
 * Sent messages for current user.
 * @param {string} uid
 * @param {(items: Array) => void} onChange
 * @returns {() => void}
 */
export function subscribeMySent(uid, onChange) {
    if (!uid) {
        onChange([]);
        return () => {};
    }
    const q = query(collection(db, OPERATOR_MESSAGES_COLL), where('fromUid', '==', uid));
    return onSnapshot(
        q,
        (snap) => {
            const items = snap.docs.map(mapMessageDoc).sort(sortByCreatedDesc);
            onChange(items);
        },
        (err) => {
            console.error('[operatorMessage] sent subscribe failed:', err);
            onChange([]);
        }
    );
}

export function formatOperatorMessageTime(value) {
    const ms = toMillis(value);
    if (!ms) return '';
    try {
        return new Intl.DateTimeFormat('en-GB', {
            dateStyle: 'medium',
            timeStyle: 'short',
            timeZone: 'Europe/London',
        }).format(new Date(ms));
    } catch {
        return new Date(ms).toLocaleString();
    }
}

export { displayNameFromUser, userHasAdminOrProductionRole };
